import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import useAuthState, { Tenant } from './auth.state';
import { env } from '@/utils/constants/env';
import { axiosInstance } from '@/utils/lib/axios';
import { toast } from 'react-toastify';
import useLicenseState from './license.state';


type Store = {
  currentTenant: Tenant | null;
  setCurrentTenant: (tenant: Tenant | null) => void;
  removeCurrentTenant: () => void;
  createNewWorkspace: (name: string) => Promise<void>;
  deleteWorkspace: (tenantId: number) => Promise<any>;
  homepageCreateNewWorkspace: boolean;
  setHomepageCreateNewWorkspace: (value: boolean) => void;
};

const useTenantState = create<Store>()(
  persist(
    set => ({
      currentTenant: null,
      homepageCreateNewWorkspace: false,
      setCurrentTenant: (tenant: Tenant | null) => {
        localStorage.setItem('tenantId', tenant ? tenant.id.toString() : '');
        set({ currentTenant: tenant })
      },
      removeCurrentTenant: () => set({ currentTenant: null }),
      createNewWorkspace: async (name: string) => {
        // Quota check: enforce max_workspaces from license
        const maxWorkspaces = useLicenseState.getState().getMaxWorkspaces();
        const currentTenants = useAuthState.getState().tenants ?? [];
        if (maxWorkspaces > 0 && currentTenants.length >= maxWorkspaces) {
          toast.error(
            `Quota workspace penuh (${currentTenants.length}/${maxWorkspaces}). Upgrade lisensi Anda untuk menambah workspace lebih banyak.`,
            { position: 'top-right' }
          );
          return;
        }

        // API call to create a new workspace can be added here
        const payload = {
          name: name,
        };
        try {
          const token = await useAuthState.getState().getToken();
          if (!token) throw new Error('No authentication token');
          
          const response = await axiosInstance.post(`${env.apiTasklistBaseUrl}/api/v1/tenant/tenants`, payload);
          console.log('Workspace created:', response.data);
          await useAuthState.getState().loadTenants(token);
          set({ homepageCreateNewWorkspace: false });
          toast.success('Workspace created successfully',{position: "top-right"})
        } catch (error: any) {
          console.error('Error creating workspace:', error);
          if (error.response && error.response.status === 400) {
            const errorMessage = error.response.data.error;
            toast.error(errorMessage, { position: "top-right" });
          } else {
            toast.error('Failed to create workspace. Please try again.', { position: "top-right" });
          }
        }
      },
      setHomepageCreateNewWorkspace: (value: boolean) => set({ homepageCreateNewWorkspace: value }),
      deleteWorkspace: async (tenantId: number) => {
        try {
          const result = await axiosInstance.delete(`${env.apiTasklistBaseUrl}/api/v1/tenant/tenants/${tenantId}`);
          toast.success('Workspace deleted successfully', { position: 'top-right' });
          return result.data;
        } catch (error: any) {
          console.error('Error deleting workspace:', error);
          if (error?.response?.status === 400) {
            const errorMessage = error.response.data.error;
            toast.error(errorMessage, { position: 'top-right' });
          } else {
            toast.error('Failed to delete workspace. Please try again.', { position: 'top-right' });
          }
        }
      },
    }),
    {
      name: 'current-tenant-storage', // unique name for the storage
      storage: createJSONStorage(() => localStorage), // use localStorage
    },
  ),
);

export default useTenantState;
